import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import CloseIcon from "@material-ui/icons/Close";

import {
  QUALIFYING_SCORE_DEFAULT_SORT,
  QUALIFYING_SCORE_SORT_TOKENS,
  QUALIFYING_SCORE_SORT_TOKEN_LABELS,
} from "../../constants/data";
import { COLORS } from "../../constants/styles";

const useStyles = makeStyles({
  popoverPaper: {
    padding: "12px 16px",
    minWidth: "320px",
    maxWidth: "360px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    margin: "8px 0 12px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 8px",
    border: "1px solid #e0e6ef",
    borderRadius: "6px",
    backgroundColor: "#fff",
  },
  rowDragging: {
    boxShadow: "0 4px 16px rgba(16,24,40,0.12)",
  },
  rowLabel: {
    flexGrow: 1,
    fontSize: "13px",
  },
  rowIndex: {
    width: "24px",
    color: "#667085",
    fontSize: "12px",
  },
  handle: {
    cursor: "grab",
    color: "#98a2b3",
  },
  addRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "4px",
  },
  controls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "8px",
  },
  actions: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
    marginTop: "12px",
  },
  resetLink: {
    fontSize: "12px",
    color: COLORS?.TEXT_GREEN || "#1f8a4c",
    cursor: "pointer",
    textDecoration: "underline",
    border: "none",
    background: "transparent",
    padding: 0,
  },
});

const SortableRow = ({ id, label, index, onRemove, removeDisabled }) => {
  const classes = useStyles();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      className={`${classes.row} ${isDragging ? classes.rowDragging : ""}`}
      role="listitem"
      aria-label={`Sort priority ${index + 1}: ${label}`}
    >
      <Box
        className={classes.handle}
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        tabIndex={0}
      >
        <DragHandleIcon fontSize="small" />
      </Box>
      <Typography className={classes.rowIndex}>{index + 1}.</Typography>
      <Typography className={classes.rowLabel}>{label}</Typography>
      <IconButton
        size="small"
        onClick={onRemove}
        disabled={removeDisabled}
        aria-label={`Remove ${label} from sort`}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

SortableRow.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  removeDisabled: PropTypes.bool,
};

const QualifyingScoreSortPopover = ({
  anchorEl,
  open,
  onClose,
  sortTokens,
  isAscending,
  onApply,
}) => {
  const classes = useStyles();
  const [draftTokens, setDraftTokens] = useState(sortTokens);
  const [draftIsAscending, setDraftIsAscending] = useState(isAscending);
  const [addValue, setAddValue] = useState("");

  useEffect(() => {
    if (open) {
      setDraftTokens(sortTokens?.length ? sortTokens : QUALIFYING_SCORE_DEFAULT_SORT);
      setDraftIsAscending(isAscending);
      setAddValue("");
    }
  }, [open, sortTokens, isAscending]);

  const availableToAdd = useMemo(
    () =>
      QUALIFYING_SCORE_SORT_TOKENS.filter((token) => !draftTokens.includes(token)),
    [draftTokens],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = draftTokens.indexOf(active.id);
    const newIndex = draftTokens.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setDraftTokens(arrayMove(draftTokens, oldIndex, newIndex));
  };

  const handleRemove = (token) => {
    if (draftTokens.length <= 1) return;
    setDraftTokens(draftTokens.filter((t) => t !== token));
  };

  const handleAdd = () => {
    if (!addValue) return;
    setDraftTokens([...draftTokens, addValue]);
    setAddValue("");
  };

  const handleReset = () => {
    setDraftTokens(QUALIFYING_SCORE_DEFAULT_SORT);
    setDraftIsAscending(true);
  };

  const handleApply = () => {
    onApply({ sortTokens: draftTokens, isAscending: draftIsAscending });
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{ className: classes.popoverPaper }}
    >
      <Box className={classes.header}>
        <Typography variant="subtitle2">Edit sort</Typography>
        <button
          type="button"
          className={classes.resetLink}
          onClick={handleReset}
        >
          Reset to default
        </button>
      </Box>

      <Box className={classes.controls}>
        <Typography variant="body2">Direction (shared)</Typography>
        <Select
          value={draftIsAscending ? "asc" : "desc"}
          onChange={(e) => setDraftIsAscending(e.target.value === "asc")}
          variant="outlined"
          margin="dense"
          style={{ minWidth: 120 }}
        >
          <MenuItem value="asc">Ascending</MenuItem>
          <MenuItem value="desc">Descending</MenuItem>
        </Select>
      </Box>

      <Box className={classes.list} role="list">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={draftTokens} strategy={verticalListSortingStrategy}>
            {draftTokens.map((token, index) => (
              <SortableRow
                key={token}
                id={token}
                index={index}
                label={QUALIFYING_SCORE_SORT_TOKEN_LABELS[token] || token}
                onRemove={() => handleRemove(token)}
                removeDisabled={draftTokens.length <= 1}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Box>

      {availableToAdd.length > 0 && (
        <Box className={classes.addRow}>
          <Select
            value={addValue}
            onChange={(e) => setAddValue(e.target.value)}
            displayEmpty
            variant="outlined"
            margin="dense"
            style={{ flexGrow: 1 }}
          >
            <MenuItem value="" disabled>
              Add field…
            </MenuItem>
            {availableToAdd.map((token) => (
              <MenuItem key={token} value={token}>
                {QUALIFYING_SCORE_SORT_TOKEN_LABELS[token] || token}
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="outlined"
            size="small"
            onClick={handleAdd}
            disabled={!addValue}
          >
            Add
          </Button>
        </Box>
      )}

      <Box className={classes.actions}>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="primary" variant="contained" onClick={handleApply}>
          Apply
        </Button>
      </Box>
    </Popover>
  );
};

QualifyingScoreSortPopover.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sortTokens: PropTypes.arrayOf(PropTypes.string).isRequired,
  isAscending: PropTypes.bool.isRequired,
  onApply: PropTypes.func.isRequired,
};

export default QualifyingScoreSortPopover;
